import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { buildApiUrl } from "../utils/api";

function WarehouseEnquiryList() {
    const [briefs, setBriefs] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRow, setExpandedRow] = useState(null);
    const itemsPerPage = 10;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(buildApiUrl("/api/getFinders"));
                const data = Array.isArray(res.data) ? res.data : [];
                setBriefs(data);
                setFiltered(data);
            } catch (error) {
                console.error("Error fetching buyer briefs", error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const q = search.toLowerCase().trim();
        if (!q) {
            setFiltered(briefs);
        } else {
            setFiltered(
                briefs.filter(b =>
                    (b.name || "").toLowerCase().includes(q) ||
                    (b.email || "").toLowerCase().includes(q) ||
                    (b.firmName || "").toLowerCase().includes(q) ||
                    (b.location || "").toLowerCase().includes(q)
                )
            );
        }
        setCurrentPage(1);
    }, [search, briefs]);

    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    const currentItems = filtered.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };

    return (
        <>
            <Helmet>
                <title>Buyer Briefs | CS Agent</title>
            </Helmet>

            <div className="listHead">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h1 className="mb-0">
                        Buyer Briefs{" "}
                        <span className="badge bg-secondary fs-6">{filtered.length}</span>
                    </h1>
                    <input
                        type="text"
                        className="form-control"
                        style={{ minWidth: "240px", maxWidth: "320px" }}
                        placeholder="Search name, email, firm, location..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                <div className="table-responsive">
                    <table className="table table-bordered table-hover">
                        <thead className="custom-thead">
                            <tr>
                                <th>#</th>
                                <th>Date</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Company</th>
                                <th>Location</th>
                                <th>SKU</th>
                                <th>Orders/mo</th>
                                <th>Product Type</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length > 0 ? (
                                currentItems.map((b, index) => (
                                    <React.Fragment key={b._id}>
                                        <tr>
                                            <td>{indexOfFirst + index + 1}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>{formatDate(b.createdAt)}</td>
                                            <td>{b.name}</td>
                                            <td><a href={`mailto:${b.email}`}>{b.email}</a></td>
                                            <td>{b.contactNumber}</td>
                                            <td>{b.firmName}</td>
                                            <td>{b.location}</td>
                                            <td>{b.totalSku}</td>
                                            <td>{b.avgMonthlyOrders}</td>
                                            <td>{b.productType || "-"}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setExpandedRow(expandedRow === b._id ? null : b._id)}
                                                >
                                                    {expandedRow === b._id ? "Hide" : "View"}
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedRow === b._id && (
                                            <tr className="table-light">
                                                <td colSpan="11">
                                                    <div className="p-3">
                                                        <div className="row g-3">
                                                            <div className="col-md-4">
                                                                <strong>Sales Channel:</strong> {b.salesChannel || "-"}<br />
                                                                <strong>Service Needs:</strong> {b.serviceNeeds || "-"}<br />
                                                                <strong>Storage Type:</strong> {b.storageType || "-"}<br />
                                                                <strong>Fulfillment Regions:</strong> {b.fulfillmentRegions || "-"}
                                                            </div>
                                                            <div className="col-md-4">
                                                                <strong>Temperature Req:</strong> {b.temperatureRequirement || "-"}<br />
                                                                <strong>Go-Live Timeline:</strong> {b.preferredGoLiveDate || "-"}<br />
                                                                <strong>Preferred Contact:</strong> {b.preferredContactMethod || "-"}<br />
                                                                <strong>Website:</strong>{" "}
                                                                {b.firmWebsite
                                                                    ? <a href={b.firmWebsite} target="_blank" rel="noreferrer">{b.firmWebsite}</a>
                                                                    : "-"}
                                                            </div>
                                                            <div className="col-md-4">
                                                                <strong>Requirement Summary:</strong>
                                                                <p className="mt-1 mb-0" style={{ whiteSpace: "pre-wrap" }}>{b.summary || "-"}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="11" className="text-center text-muted py-4">No buyer briefs found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <nav className="d-flex justify-content-center">
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>&laquo;</button>
                            </li>
                            {Array.from({ length: totalPages }, (_, i) => (
                                <li className={`page-item ${currentPage === i + 1 ? "active" : ""}`} key={i}>
                                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>&raquo;</button>
                            </li>
                        </ul>
                    </nav>
                )}
            </div>
        </>
    );
}

export default WarehouseEnquiryList;
